/**
 * CNC PM PWA - Google Sheets HTTP Client
 * Dispatches inspection records and connectivity checks to Google Apps Script Web App.
 * Per PROJECT.md § Interface Contracts & ORIGINAL_REQUEST.md § R5.
 */

import type {
  InspectionRecord,
  GasSyncPayload,
  GasSyncResponse,
} from '../../types';

export interface GoogleConnectionTestResult {
  success: boolean;
  message: string;
  spreadsheetId?: string;
  sheetsFound?: string[];
  isOffline?: boolean;
  rawResponse?: any;
}

export interface UrlValidationResult {
  valid: boolean;
  trimmedUrl: string;
  error?: string;
  warning?: string;
}

/**
 * Validates and sanitizes a Google Apps Script Web App URL
 */
export function validateGoogleWebAppUrl(url: string): UrlValidationResult {
  const trimmedUrl = (url || '').trim();

  if (!trimmedUrl) {
    return {
      valid: false,
      trimmedUrl,
      error: 'กรุณากรอก Google Apps Script Web App URL',
    };
  }

  if (!trimmedUrl.startsWith('https://')) {
    return {
      valid: false,
      trimmedUrl,
      error: 'URL ต้องขึ้นต้นด้วยโปรโตคอลความปลอดภัย https://',
    };
  }

  let warning: string | undefined;
  if (!/^https:\/\/script\.google\.com\/macros\/s\//i.test(trimmedUrl)) {
    warning = 'คำเตือน: URL ไม่ได้อยู่ในโดเมนมาตรฐานของ Google Apps Script (script.google.com)';
  }

  return {
    valid: true,
    trimmedUrl,
    warning,
  };
}

/**
 * Sends a test_connection action to verify Web App connectivity and sheet access
 */
export async function testGoogleSheetsConnection(
  webAppUrl: string
): Promise<GoogleConnectionTestResult> {
  const validation = validateGoogleWebAppUrl(webAppUrl);
  if (!validation.valid) {
    return {
      success: false,
      message: validation.error || 'URL ไม่ถูกต้อง',
    };
  }

  // Check offline status
  if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' && !navigator.onLine) {
    return {
      success: false,
      message: 'อุปกรณ์ออฟไลน์อยู่: ไม่สามารถทดสอบการเชื่อมต่อไปยัง Google Sheets ได้ขณะไม่มีเครือข่าย',
      isOffline: true,
    };
  }

  const payload: GasSyncPayload = {
    action: 'test_connection',
    timestamp: new Date().toISOString(),
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    // Use text/plain to prevent unnecessary CORS preflight redirects from GAS
    const response = await fetch(validation.trimmedUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      return {
        success: false,
        message: `เชื่อมต่อไม่สำเร็จ (HTTP ${response.status}: ${response.statusText})`,
      };
    }

    const json = await response.json();

    const isSuccess = json.success === true || json.status === 'ok' || json.code === 'CONNECTION_VERIFIED';
    const message = json.message || (isSuccess ? 'เชื่อมต่อกับ Google Sheet สำเร็จแล้ว' : 'เซิร์ฟเวอร์ส่งการตอบกลับผิดพลาด');

    return {
      success: isSuccess,
      message,
      spreadsheetId: json.spreadsheetId,
      sheetsFound: json.sheetsFound,
      rawResponse: json,
    };
  } catch (err: any) {
    if (err.name === 'AbortError') {
      return {
        success: false,
        message: 'การเชื่อมต่อหมดเวลา (Timeout 15 วินาที) โปรดตรวจสอบอินเทอร์เน็ตหรือ URL',
      };
    }

    return {
      success: false,
      message: `ไม่สามารถติดต่อ Web App Endpoint ได้: ${err.message || 'Network error'}`,
    };
  }
}

/**
 * Dispatches a single inspection record to Google Sheets
 */
export async function sendSingleInspection(
  webAppUrl: string,
  record: InspectionRecord
): Promise<GasSyncResponse> {
  const validation = validateGoogleWebAppUrl(webAppUrl);
  if (!validation.valid) {
    throw new Error(validation.error || 'Invalid Google Apps Script Web App URL');
  }

  const payload: GasSyncPayload = {
    action: 'single_inspection',
    timestamp: new Date().toISOString(),
    inspections: [record],
  };

  const response = await fetch(validation.trimmedUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain;charset=utf-8',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP Error ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();
  const success = result.success === true || result.status === 'ok';

  return {
    success,
    message: result.message || (success ? 'Successfully ingested 1 inspection record(s)' : 'Sync failed'),
    processedCount: result.processedCount != null ? result.processedCount : (success ? 1 : 0),
    timestamp: result.timestamp || new Date().toISOString(),
  };
}

/**
 * Dispatches a batch of inspection records to Google Sheets
 */
export async function sendBatchInspections(
  webAppUrl: string,
  records: InspectionRecord[]
): Promise<GasSyncResponse> {
  const validation = validateGoogleWebAppUrl(webAppUrl);
  if (!validation.valid) {
    throw new Error(validation.error || 'Invalid Google Apps Script Web App URL');
  }

  if (!Array.isArray(records) || records.length === 0) {
    return {
      success: true,
      message: 'No inspection records to sync',
      processedCount: 0,
      timestamp: new Date().toISOString(),
    };
  }

  const payload: GasSyncPayload = {
    action: 'batch_inspections',
    timestamp: new Date().toISOString(),
    inspections: records,
  };

  const response = await fetch(validation.trimmedUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain;charset=utf-8',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP Error ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();
  const success = result.success === true || result.status === 'ok';

  return {
    success,
    message: result.message || (success ? `Successfully batch processed ${records.length} inspection record(s)` : 'Batch sync failed'),
    processedCount: result.processedCount != null ? result.processedCount : (success ? records.length : 0),
    timestamp: result.timestamp || new Date().toISOString(),
  };
}

/**
 * Health check ping to verify the Google Apps Script Web App is active
 */
export async function pingGoogleSheetsEndpoint(
  webAppUrl: string
): Promise<{ status: string; service: string; version: string; timestamp: string }> {
  const validation = validateGoogleWebAppUrl(webAppUrl);
  if (!validation.valid) {
    throw new Error(validation.error || 'Invalid Google Apps Script Web App URL');
  }

  const pingUrl = validation.trimmedUrl.includes('?')
    ? `${validation.trimmedUrl}&action=ping`
    : `${validation.trimmedUrl}?action=ping`;

  const response = await fetch(pingUrl, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Ping failed with HTTP ${response.status}: ${response.statusText}`);
  }

  return await response.json();
}

export interface CloudInspectionItem {
  itemNo: number | string;
  taskNameTh: string;
  criteriaTh?: string;
  methodTh?: string;
  measuredValue?: string;
  status: 'OK' | 'NG' | 'DONE' | 'N/A';
  minValue?: number;
  maxValue?: number;
  notes?: string;
  photoUrl?: string;
  hasPhoto?: boolean;
}

export interface CloudInspectionRecord {
  id: string;
  submissionId: string;
  inspectionDate: string;
  syncDate?: string;
  machineId: string;
  machineName: string;
  machineModel?: string;
  inspectorName: string;
  frequency: string;
  runningHours?: number;
  overallResult: 'PASS' | 'FAIL' | 'OK' | 'NG';
  supervisorNotes?: string;
  photoCount: number;
  items: CloudInspectionItem[];
}

export interface FetchHistoryResult {
  success: boolean;
  status?: string;
  message?: string;
  count: number;
  inspections: CloudInspectionRecord[];
}

/**
 * Fetch past inspection records and photos from Google Sheets
 */
export async function fetchInspectionHistory(
  webAppUrl: string,
  limit: number = 50,
  machineId?: string
): Promise<FetchHistoryResult> {
  const validation = validateGoogleWebAppUrl(webAppUrl);
  if (!validation.valid) {
    throw new Error(validation.error || 'Invalid Google Apps Script Web App URL');
  }

  // Use POST to avoid query string length or caching issues, with GET fallback
  const payload = {
    action: 'get_history',
    limit,
    machineId: machineId || '',
    timestamp: new Date().toISOString(),
  };

  try {
    const response = await fetch(validation.trimmedUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    const isOldVersion = data.code === 'UNKNOWN_ACTION' || (data.message && data.message.includes('Unknown action'));
    const message = isOldVersion
      ? 'สคริปต์ Google Apps Script บนคลาวด์ยังเป็นเวอร์ชันเดิม (v1.0.0) กรุณานำโค้ด gas/Code.gs (v1.1.0) ไปอัปเดตใน Apps Script แล้วกด Deploy -> Manage deployments -> เลือก New version'
      : (data.message || (data.success ? 'ดึงข้อมูลสำเร็จ' : 'ไม่สามารถดึงข้อมูลได้'));

    return {
      success: data.success === true && Array.isArray(data.inspections),
      status: data.status,
      message,
      count: data.count || (data.inspections ? data.inspections.length : 0),
      inspections: Array.isArray(data.inspections) ? data.inspections : [],
    };
  } catch (postError: any) {
    // Try GET fallback
    const getUrl = new URL(validation.trimmedUrl);
    getUrl.searchParams.set('action', 'get_history');
    getUrl.searchParams.set('limit', String(limit));
    if (machineId) getUrl.searchParams.set('machineId', machineId);

    const getResp = await fetch(getUrl.toString(), { method: 'GET' });
    if (!getResp.ok) {
      throw new Error(`Fetch history failed: ${postError.message || 'Unknown network error'}`);
    }

    const getData = await getResp.json();
    const isOldVersion = getData.version === '1.0.0' || !Array.isArray(getData.inspections);
    const message = isOldVersion
      ? 'สคริปต์ Google Apps Script บนคลาวด์ยังเป็นเวอร์ชันเดิม (v1.0.0) กรุณานำโค้ด gas/Code.gs (v1.1.0) ไปอัปเดตใน Apps Script แล้วกด Deploy -> Manage deployments -> เลือก New version'
      : (getData.message || (getData.success ? 'ดึงข้อมูลสำเร็จ' : 'ไม่สามารถดึงข้อมูลได้'));

    return {
      success: getData.success === true && Array.isArray(getData.inspections),
      status: getData.status,
      message,
      count: getData.count || (getData.inspections ? getData.inspections.length : 0),
      inspections: Array.isArray(getData.inspections) ? getData.inspections : [],
    };
  }
}
